import React from "react";

type ModulesConfigElem = {
	moduleName: string;
	moduleCategory?: string;
	moduleWeight?: number;
	moduleOrder?: number;
};

const matchesKey = (
	moduleName: string,
	key: string,
	aliasList: string[] = [],
) =>
	moduleName === key ||
	moduleName.endsWith(`_${key}`) ||
	moduleName.includes(key) ||
	aliasList.some((a) => moduleName.includes(a));

const percentsToAbsolute = (panesLen: number, percents: number[]) => {
	if (panesLen === 1) return [100];
	if (panesLen === 2) return [percents[0], Math.max(0, 100 - percents[0])];
	if (panesLen === 3)
		return [
			percents[0],
			percents[1],
			Math.max(0, 100 - percents[0] - percents[1]),
		];
	return [];
};

// Обновляет modulesConfig в localStorage, устанавливая moduleWeight для найденных moduleName
export const calculateCurrrentWeight = (
	panes: React.ReactNode[] | undefined,
	percentsState: number[] | undefined,
	aliases?: Record<string, string[]>,
) => {
	try {
		if (!panes || panes.length === 0) return null;

		const paneKeys = panes
			.map((p) => String((p as React.ReactElement)?.key ?? "").trim())
			.filter(Boolean);
		if (paneKeys.length === 0) return null;

		// Если нам передали уже абсолютные проценты (по количеству панелей), используем их напрямую.
		let absPercents: number[] = [];
		if (
			Array.isArray(percentsState) &&
			percentsState.length === paneKeys.length
		) {
			absPercents = percentsState.map((p) => Number(p));
		} else {
			absPercents = percentsToAbsolute(paneKeys.length, percentsState ?? []);
		}
		if (absPercents.length === 0) return null;

		// Сохраним дробные проценты (3 знака) как moduleWeight — более точное представление
		const percents = absPercents.map((p) => Number(p.toFixed(3)));

		// debug
		// console.debug("calculateCurrentWeight percents:", percents);

		// Прочитаем текущий modulesConfig из localStorage
		if (typeof window === "undefined") return { percents };

		const raw = localStorage.getItem("modulesConfig");
		let parsed: ModulesConfigElem[] = [];
		try {
			parsed = raw ? (JSON.parse(raw) as ModulesConfigElem[]) : [];
		} catch (e) {
			parsed = [];
		}

		// Обновим найденные записи по порядку paneKeys -> weights
		const updated = parsed.map((cfg) => ({ ...cfg }));

		// Локальные алиасы по-умолчанию: dialog <-> chat_window — чтобы поддержать оба имени
		const defaultAliases: Record<string, string[]> = {
			dialog: ["chat_window"],
			chat_window: ["dialog"],
		};

		paneKeys.forEach((key, i) => {
			const w = percents[i] ?? 0;
			const aliasList = aliases?.[key] ?? defaultAliases[key] ?? [];
			const idx = updated.findIndex(
				(c) => c && c.moduleName && matchesKey(c.moduleName, key, aliasList),
			);
			if (idx !== -1) {
				updated[idx].moduleWeight = Number(w);
			}
		});

		// Сохраняем обратно
		try {
			localStorage.setItem("modulesConfig", JSON.stringify(updated));
		} catch (e) {
			// noop
		}

		return { percents, updatedConfig: updated };
	} catch (err) {
		// eslint-disable-next-line no-console
		console.warn("calculateCurrrentWeight failed", err);
		return null;
	}
};

export default calculateCurrrentWeight;
